import { useCallback, useEffect, useState } from 'react';
import { SavedBuyPlan, SavedSellPlan } from '../types/savedPlans';

/**
 * Persistence for saved Split Buy / Split Sell plans. Mirrors the useTaxPlan
 * pattern: frozen snapshots in a localStorage array, a same-document sync event so
 * every mounted instance (the planner that saves + the summary band) stays in sync,
 * and append-only adds (multiple plans per coin are allowed, never overwritten).
 */

const BUY_KEY  = 'savedBuyPlans';
const SELL_KEY = 'savedSellPlans';
const BUY_EVENT  = 'savedbuyplans:changed';
const SELL_EVENT = 'savedsellplans:changed';

function read<T>(key: string): T[] {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as T[]) : [];
  } catch {
    return [];
  }
}

function write<T>(key: string, event: string, items: T[]): void {
  try {
    localStorage.setItem(key, JSON.stringify(items));
  } catch {
    /* ignore quota / serialization errors */
  }
  try {
    window.dispatchEvent(new Event(event));
  } catch {
    /* ignore */
  }
}

function genId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    /* fall through */
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function useSavedPlans<T extends { id: string; savedAt: string }>(key: string, event: string) {
  const [plans, setPlans] = useState<T[]>(() => read<T>(key));

  useEffect(() => {
    const resync = () => setPlans(read<T>(key));
    const onStorage = (e: StorageEvent) => {
      if (e.key === null || e.key === key) resync();
    };
    window.addEventListener(event, resync);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(event, resync);
      window.removeEventListener('storage', onStorage);
    };
  }, [key, event]);

  const addPlan = useCallback((plan: Omit<T, 'id' | 'savedAt'>): T => {
    const full = { ...plan, id: genId(), savedAt: new Date().toISOString() } as T;
    const next = [...read<T>(key), full];
    write(key, event, next);
    setPlans(next);
    return full;
  }, [key, event]);

  const deletePlan = useCallback((id: string) => {
    const next = read<T>(key).filter((p) => p.id !== id);
    write(key, event, next);
    setPlans(next);
  }, [key, event]);

  // Update an existing plan in place (matched by id, position preserved). Used by
  // the "edit → re-save" flow so editing never creates a duplicate. savedAt is
  // refreshed to the update time.
  const updatePlan = useCallback((id: string, patch: Omit<T, 'id' | 'savedAt'>): void => {
    const next = read<T>(key).map((p) =>
      p.id === id ? ({ ...patch, id, savedAt: new Date().toISOString() } as T) : p
    );
    write(key, event, next);
    setPlans(next);
  }, [key, event]);

  return { plans, addPlan, deletePlan, updatePlan };
}

export function useSavedBuyPlans() {
  const { plans, addPlan, deletePlan, updatePlan } = useSavedPlans<SavedBuyPlan>(BUY_KEY, BUY_EVENT);
  const getTotal = useCallback(
    () => plans.reduce((sum, p) => sum + (p.totalBudget || 0), 0),
    [plans]
  );
  return { plans, addPlan, deletePlan, updatePlan, getTotal };
}

export function useSavedSellPlans() {
  const { plans, addPlan, deletePlan, updatePlan } = useSavedPlans<SavedSellPlan>(SELL_KEY, SELL_EVENT);
  const getTotal = useCallback(
    () => plans.reduce((sum, p) => sum + (p.expectedProceeds || 0), 0),
    [plans]
  );
  return { plans, addPlan, deletePlan, updatePlan, getTotal };
}
